// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize for lower per-GB pricing on both mobile ($2/GB flat) and residential ($1/GB flat) proxies with self-serve setup. Choose Decodo for much broader global mobile coverage (700+ mobile networks across 160+ locations), ISP and datacenter proxies, or its bundled scraping and AI data tooling.";

export const KEY_TAKEAWAYS = [
  "Proxidize undercuts Decodo's published mobile and residential rates at most volumes. Decodo runs $2.75–3.75/GB mobile and $3.00–3.75/GB residential, versus Proxidize's flat $2/GB and $1/GB.",
  "Decodo covers far more mobile locations. 700+ mobile networks across 160+ locations, versus Proxidize's five countries.",
  "Decodo has a smaller entry-level mobile bundle. 2 GB for $7.50 (or pay-as-you-go from $4/GB), versus Proxidize's 25 GB / $50 minimum.",
  "Decodo sells ISP and datacenter proxies Proxidize doesn't yet offer. From $2.50/IP and $0.026/IP respectively.",
  "Decodo bundles a scraping and AI data platform. Web Scraping API, Site Unblocker, AI Parser, and an MCP server; Proxidize is proxy-focused.",
  "Both providers are enterprise-grade on trust and security, with comparable review scores. Proxidize: SOC 2 Type II, ISO 27001, 4.8 Trustpilot / 4.7 G2. Decodo: 4.4 Trustpilot / 4.6 G2 across roughly 2,500 combined reviews.",
];

export interface VerdictCard {
  provider: "proxidize" | "decodo";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs across multiple countries at a flat $2/GB with city-level targeting",
      "Residential proxies in 195+ countries at a flat $1/GB with rollover bandwidth",
      "Lower published per-GB rates than Decodo on both mobile and residential at most volumes",
      "A per-proxy mobile plan from $59/proxy/month with unlimited bandwidth and a stable exit",
      "Manual/bulk rotation, an API rotation URL, and custom interval control",
      "Self-serve setup: credentials in under a minute, no sales call",
      "Free sign-up without a credit card",
    ],
  },
  {
    provider: "decodo",
    title: "Choose Decodo if you need",
    points: [
      "Global mobile coverage: 10M+ mobile IPs from 700+ mobile networks across 160+ locations",
      "Small entry-point mobile bundles (2 GB, 8 GB, 25 GB) for light or one-off usage",
      "ISP (static residential) and datacenter proxies alongside mobile and residential",
      "A broader data platform: Web Scraping API, Site Unblocker, AI Parser, Fast Search API, MCP server",
      "ZIP-code and ASN-level targeting on top of country/city",
      "One vendor for proxies plus scraping and AI data tooling",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  decodo: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Mobile IPs, self-serve simplicity",
    decodo: "Global proxy coverage plus a broader scraping/data platform",
  },
  {
    feature: "Mobile network",
    proxidize: "Premium pooled mobile IPs, 50+ locations across multiple countries",
    decodo: "10M+ IPs from 700+ mobile networks, 160+ global locations",
  },
  {
    feature: "Mobile country coverage",
    proxidize: "US, Canada, UK, Germany, India",
    decodo: "160+ locations worldwide",
  },
  {
    feature: "Mobile targeting",
    proxidize: "City + ASN",
    decodo: "Country, city, ASN, ZIP, and OS (iOS/Android)",
  },
  {
    feature: "Mobile pricing",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    decodo: "$3.75 to $2.75/GB by plan (2 GB to 100 GB)",
  },
  {
    feature: "100 GB mobile plan",
    proxidize: "$200/mo ($2.00/GB)",
    decodo: "$275/mo ($2.75/GB)",
  },
  {
    feature: "Smallest mobile entry point",
    proxidize: "25 GB for $50",
    decodo: "2 GB for $7.50, or pay-as-you-go from $4/GB",
  },
  {
    feature: "Per-proxy mobile plans",
    proxidize: "Yes, from $59/proxy/month with unlimited bandwidth",
    decodo: "Not publicly listed — pricing is bandwidth/package based",
  },
  {
    feature: "Sticky sessions",
    proxidize: "Sticky or random per access point",
    decodo: "Rotating per request, or sticky sessions",
  },
  {
    feature: "Rotation control",
    proxidize: "Custom interval (min 1 min), manual, API rotation URL",
    decodo: "Automatic rotation or sticky mode",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    decodo: "HTTP(S), SOCKS5",
  },
  { feature: "Self-serve signup", proxidize: true, decodo: true },
  {
    feature: "Free trial",
    proxidize: "Free sign-up, no credit card",
    decodo: "3-day trial (100 MB)",
  },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    decodo: "115M+ IPs, 195+ locations",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    decodo: "$3.75 to $3.00/GB by plan (3 GB to 50 GB)",
  },
  {
    feature: "50 GB residential plan",
    proxidize: "$50/mo ($1.00/GB)",
    decodo: "$150/mo ($3.00/GB)",
  },
  {
    feature: "Rollover of unused residential bandwidth",
    proxidize: true,
    decodo: "See their terms",
  },
  {
    feature: "ISP (static residential) proxies",
    proxidize: "Not yet offered",
    decodo: "From $2.50/IP (200-IP plan), premium ASNs",
  },
  {
    feature: "Datacenter proxies",
    proxidize: "Not yet offered",
    decodo: "125M+ IPs, from $0.026/IP or $0.47/GB",
  },
  { feature: "Scraper APIs / AI data tooling", proxidize: false, decodo: true },
  {
    feature: "Security certifications",
    proxidize: "SOC 2 Type II, ISO 27001",
    decodo: "See their trust center",
  },
  { feature: "Trustpilot rating", proxidize: "4.8/5", decodo: "4.4/5 (~2,000 reviews)" },
  { feature: "G2 rating", proxidize: "4.7/5", decodo: "4.6/5 (546 reviews)" },
  {
    feature: "Money-back guarantee",
    proxidize: "30 days (usage & policy limits)",
    decodo: "14 days (terms apply)",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "Decodo" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Mobile scraping in Proxidize's covered countries",
    fit: "Proxidize",
    reason:
      "Premium pooled mobile IPs with city targeting at a lower per-GB rate, plus per-proxy plans for a stable exit.",
  },
  {
    useCase: "Global, multi-country mobile scraping",
    fit: "Decodo",
    reason:
      "160+ locations and 700+ mobile networks worldwide vs Proxidize's smaller, growing footprint.",
  },
  {
    useCase: "Very small or one-off mobile testing",
    fit: "Decodo",
    reason:
      "2 GB bundles and pay-as-you-go pricing fit tiny usage better than Proxidize's 25 GB minimum.",
  },
  {
    useCase: "Stable exits for account-based workflows",
    fit: "Proxidize",
    reason:
      "Per-proxy plans give a consistent mobile IP you control, with unlimited bandwidth and custom rotation.",
  },
  {
    useCase: "Lower-cost residential scraping at scale",
    fit: "Proxidize",
    reason:
      "Flat $1/GB with rollover bandwidth beats Decodo's $3.00–$3.75/GB published residential tiers.",
  },
  {
    useCase: "One vendor for proxies plus scraping APIs and AI tooling",
    fit: "Decodo",
    reason:
      "Decodo bundles Web Scraping API, Site Unblocker, AI Parser, Fast Search API, and an MCP server; Proxidize is proxy-focused.",
  },
  {
    useCase: "Long-lived static IPs or datacenter-scale scraping",
    fit: "Decodo",
    reason:
      "Decodo publicly offers ISP and datacenter proxies; Proxidize doesn't currently offer either.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call.",
  },
  {
    title: "Pick per-GB or per-proxy",
    description:
      "Per-GB gives flexible access to the full pool billed by data. Per-proxy gives you real mobile IPs of your own with unlimited bandwidth.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a city or random rotation and set sticky or random IP behavior, the same knobs you tuned in the Decodo dashboard.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
